import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import { getErrorMessage } from '../utils/errorHandler';
import { CreditCard, Tag, Check, AlertCircle } from 'lucide-react';

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planType = searchParams.get('plan') || 'professional';
  const [paymentType, setPaymentType] = useState('down_payment');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [loading, setLoading] = useState(false);

  const plans = {
    starter: {
      name: 'Starter AI System',
      full_price: 4997,
      down_payment: 1999,
      balance: 2998
    },
    professional: {
      name: 'Professional AI Suite',
      full_price: 12997,
      down_payment: 5199,
      balance: 7798
    },
    enterprise: {
      name: 'Enterprise AI Platform',
      full_price: 29997,
      down_payment: 11999,
      balance: 17998
    }
  };

  const currentPlan = plans[planType] || plans.professional;

  const getAmount = () => {
    return paymentType === 'down_payment' ? currentPlan.down_payment : currentPlan.full_price;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponError('');
    setLoading(true);

    try {
      const amount = getAmount();
      const response = await api.post('/api/coupons/validate', {
        code: couponCode.toUpperCase(),
        plan_type: planType,
        amount: amount
      });

      setCouponApplied(response.data);
    } catch (error) {
      console.error('Coupon validation error:', error);
      setCouponError(getErrorMessage(error));
      setCouponApplied(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);

    try {
      const originUrl = window.location.origin;
      const response = await api.post('/api/checkout/session', {
        plan_type: planType,
        payment_type: paymentType,
        coupon_code: couponApplied ? couponCode.toUpperCase() : null,
        origin_url: originUrl
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(getErrorMessage(error));
      setLoading(false);
    }
  };

  const originalAmount = getAmount();
  const discountAmount = couponApplied ? couponApplied.discount_amount : 0;
  const finalAmount = couponApplied ? couponApplied.final_amount : originalAmount;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-page)' }}>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="heading-1 mb-2">Checkout</h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Complete your purchase to start your AI transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Left: Plan Details */}
            <div>
              <div className="voice-card mb-6">
                <h2 className="heading-3 mb-4">Plan Details</h2>
                <div className="mb-4">
                  <div className="heading-2 mb-2" style={{ color: 'var(--accent-orange-400)' }}>
                    {currentPlan.name}
                  </div>
                  <div className="body-large font-semibold">${currentPlan.full_price.toLocaleString()}</div>
                  <div className="caption" style={{ color: 'var(--text-muted)' }}>
                    One-time implementation fee
                  </div>
                </div>

                <div className="border-t pt-4" style={{ borderColor: 'var(--border-light)' }}>
                  <h3 className="body-small font-semibold mb-3">Payment Options:</h3>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ 
                      borderColor: paymentType === 'down_payment' ? 'var(--accent-blue-400)' : 'var(--border-light)',
                      background: paymentType === 'down_payment' ? 'rgba(59, 130, 246, 0.05)' : 'transparent'
                    }}>
                      <input
                        type="radio"
                        value="down_payment"
                        checked={paymentType === 'down_payment'}
                        onChange={(e) => {
                          setPaymentType(e.target.value);
                          setCouponApplied(null);
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">40% Down Payment</div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          Pay ${currentPlan.down_payment.toLocaleString()} now, ${currentPlan.balance.toLocaleString()} upon completion
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ 
                      borderColor: paymentType === 'full_payment' ? 'var(--accent-blue-400)' : 'var(--border-light)',
                      background: paymentType === 'full_payment' ? 'rgba(59, 130, 246, 0.05)' : 'transparent'
                    }}>
                      <input
                        type="radio"
                        value="full_payment"
                        checked={paymentType === 'full_payment'}
                        onChange={(e) => {
                          setPaymentType(e.target.value);
                          setCouponApplied(null);
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">Full Payment</div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          Pay ${currentPlan.full_price.toLocaleString()} now and save on processing
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Coupon Code */}
              <div className="voice-card">
                <div className="flex items-center gap-2 mb-4">
                  <Tag size={20} />
                  <h3 className="heading-4">Have a Coupon Code?</h3>
                </div>
                
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponError('');
                    }}
                    placeholder="LAUNCH50"
                    className="flex-1 p-3 rounded-lg border mono-text"
                    style={{ borderColor: 'var(--border-light)' }}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={!couponCode.trim() || loading}
                    className="btn-secondary"
                    style={{ opacity: !couponCode.trim() ? 0.5 : 1 }}
                  >
                    Apply
                  </button>
                </div>

                {couponError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg mt-2" style={{ background: 'rgba(220, 38, 38, 0.1)' }}>
                    <AlertCircle size={16} style={{ color: '#DC2626' }} className="mt-0.5" />
                    <span className="caption" style={{ color: '#DC2626' }}>{couponError}</span>
                  </div>
                )}

                {couponApplied && (
                  <div className="flex items-start gap-2 p-3 rounded-lg mt-2" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <Check size={16} style={{ color: 'var(--accent-green-200)' }} className="mt-0.5" />
                    <div className="flex-1">
                      <div className="caption font-semibold" style={{ color: 'var(--accent-green-200)' }}>
                        Coupon "{couponApplied.coupon_code}" applied!
                      </div>
                      <div className="caption" style={{ color: 'var(--text-secondary)' }}>
                        {couponApplied.discount_type === 'percentage' 
                          ? `${couponApplied.discount_value}% off` 
                          : `$${couponApplied.discount_value} off`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Order Summary */}
            <div>
              <div className="voice-card sticky top-8">
                <h2 className="heading-3 mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="body-medium">Plan:</span>
                    <span className="body-medium font-semibold">{currentPlan.name}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="body-medium">Payment Type:</span>
                    <span className="body-medium font-semibold capitalize">
                      {paymentType === 'down_payment' ? '40% Down Payment' : 'Full Payment'}
                    </span>
                  </div>

                  <div className="border-t pt-3" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="flex justify-between mb-2">
                      <span className="body-medium">Subtotal:</span>
                      <span className="body-medium">${originalAmount.toLocaleString()}</span>
                    </div>

                    {couponApplied && (
                      <div className="flex justify-between mb-2" style={{ color: 'var(--accent-green-200)' }}>
                        <span className="body-medium">Discount:</span>
                        <span className="body-medium font-semibold">-${discountAmount.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="border-t pt-3 mt-3" style={{ borderColor: 'var(--border-light)' }}>
                      <div className="flex justify-between items-center">
                        <span className="heading-3">Total:</span>
                        <span className="heading-2" style={{ color: 'var(--accent-orange-400)' }}>
                          ${finalAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2 hover-scale"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  <CreditCard size={20} />
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </button>

                <div className="mt-4 text-center">
                  <div className="caption" style={{ color: 'var(--text-muted)' }}>
                    Secure payment powered by Stripe
                  </div>
                  <div className="caption mt-1" style={{ color: 'var(--text-muted)' }}>
                    🔒 Your payment information is encrypted and secure
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--accent-blue-200)' }}>
                  <h4 className="body-small font-semibold mb-2">What happens next?</h4>
                  <ul className="space-y-1 caption" style={{ color: 'var(--text-secondary)' }}>
                    <li>✓ Secure payment processing</li>
                    <li>✓ Account creation with dashboard access</li>
                    <li>✓ Implementation kickoff within 24 hours</li>
                    <li>✓ Dedicated project manager assigned</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
